import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useUI } from '@/stores/ui';

export type SleepSegment = {
  id: string;
  dayIdx: number; // 0..6 relative to weekStart (Monday)
  sHour: number;  // local-time hour fraction [0..24]
  eHour: number;  // local-time hour fraction [0..24]
};

function hourOfDay(ms: number): number {
  const d = new Date(ms);
  return d.getHours() + d.getMinutes() / 60 + d.getSeconds() / 3600;
}

function dayStartLocal(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

// Nur echter Nachtschlaf: Session-Start muss >= 21:00 oder < 05:00 lokal sein.
// Damit fallen Mittags-Nickerchen und "Freundin liegt tagsueber im Bett" raus.
const NIGHT_START_HOUR = 21;
const NIGHT_END_HOUR = 5;

function isNightSession(startTs: number, endTs: number): boolean {
  const startH = new Date(startTs).getHours();
  if (startH >= NIGHT_START_HOUR || startH < NIGHT_END_HOUR) return true;
  // Edge-case: startet frueh morgens (5-12) und ist lang → wahrscheinlich Durchschlafen bis in den Vormittag.
  if (startH < 12 && endTs - startTs >= 5 * 3600000) return true;
  return false;
}

export function useSleepSegments(weekStart: number): SleepSegment[] {
  const from = weekStart - 86400000;
  const to = weekStart + 8 * 86400000;
  const { data = [] } = useQuery({
    queryKey: ['sleep-range', from, to],
    queryFn: () => api.getSleepRange(from, to),
    staleTime: 5 * 60 * 1000,
  });

  return useMemo(() => {
    const out: SleepSegment[] = [];
    const weekStartDay = dayStartLocal(weekStart);
    for (const s of data) {
      if (!s.endTs) continue;
      if (!isNightSession(s.startTs, s.endTs)) continue;
      let cursor = dayStartLocal(s.startTs);
      while (cursor < s.endTs) {
        const dayEnd = cursor + 86400000;
        const segStart = Math.max(s.startTs, cursor);
        const segEnd = Math.min(s.endTs, dayEnd);
        const dayIdx = Math.round((cursor - weekStartDay) / 86400000);
        if (dayIdx >= 0 && dayIdx < 7 && segEnd > segStart) {
          const sHour = hourOfDay(segStart);
          let eHour = segEnd >= dayEnd ? 24 : hourOfDay(segEnd);
          // Tageszeit-Mitte (12-21) ausblenden: Segment hart bei 21:00 abschneiden, wenn es dort rumlungert.
          if (sHour >= 12 && sHour < NIGHT_START_HOUR) continue;
          if (eHour > 12 && eHour < NIGHT_START_HOUR && sHour < 12) eHour = 12;
          out.push({ id: `${s.id}:${dayIdx}`, dayIdx, sHour, eHour });
        }
        cursor = dayEnd;
      }
    }
    return out;
  }, [data, weekStart]);
}

export function SleepOverlay({
  segments,
  dayIdx,
  hourH,
  startHour,
  endHour,
}: {
  segments: SleepSegment[];
  dayIdx: number;
  hourH: number;
  startHour: number;
  endHour: number;
}) {
  const darkMode = useUI((s) => s.darkMode);
  const mine = segments.filter(
    (s) => s.dayIdx === dayIdx && s.eHour > startHour && s.sHour < endHour,
  );
  if (!mine.length) return null;

  const bg = darkMode ? 'oklch(0.38 0.10 255)' : 'oklch(0.95 0.03 255)';
  const ink = darkMode ? '#ffffff' : 'oklch(0.32 0.12 255)';

  return (
    <>
      {mine.map((s) => {
        const s0 = Math.max(s.sHour, startHour);
        const e0 = Math.min(s.eHour, endHour);
        const top = (s0 - startHour) * hourH;
        const height = (e0 - s0) * hourH;
        const isTiny = height < 14;
        return (
          <div
            key={s.id}
            className="absolute rounded-md pointer-events-none overflow-hidden"
            style={{
              top,
              height,
              left: 4,
              right: 4,
              background: bg,
              color: ink,
            }}
          >
            {!isTiny && (
              <div className="absolute inset-0 flex items-center justify-center font-medium leading-none text-[11px] tracking-[-0.1px]">
                Schlaf
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}
